/**
 * @file Creator Profile tab (EPD-12593, epic EPD-12592) — TanStack Query hooks.
 *
 * Wraps `organization.profileDetails.get`, which returns every Profile tab
 * section in one request.
 */

import type { RouterOutput } from "@repo/api/types";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { trpcClient } from "../trpc";

// ============================================================================
// Types — derived from the router to prevent drift
// ============================================================================

export type ProfileDetails =
  RouterOutput["organization"]["profileDetails"]["get"];
export type ProfileDetailsBasics = ProfileDetails["basics"];
export type ProfileDetailsStyle = ProfileDetails["style"];
export type ProfileDetailsCampaigns = ProfileDetails["campaigns"];
export type ProfileDetailsHighlight = ProfileDetails["highlights"][number];
export type ProfileDetailsRate = ProfileDetails["rates"][number];
export type ProfileDetailsAdAccess = ProfileDetails["ads"][number];
export type ProfileDetailsDemo = ProfileDetails["demo"];
export type ProfileDetailsOption = ProfileDetailsStyle["categories"][number];

// ============================================================================
// Query keys
// ============================================================================

export const profileDetailsKeys = {
  all: ["profile-details"] as const,
  byProfile: (organizationId: string, profileId: string) =>
    [...profileDetailsKeys.all, organizationId, profileId] as const,
};

// ============================================================================
// Queries
// ============================================================================

export function profileDetailsQueryOptions(
  organizationId: string,
  profileId: string,
) {
  return queryOptions({
    queryKey: profileDetailsKeys.byProfile(organizationId, profileId),
    queryFn: () =>
      trpcClient.organization.profileDetails.get.query({
        organizationId,
        profileId,
      }),
    enabled: !!profileId,
    staleTime: 60_000,
  });
}

export function useProfileDetailsQuery(
  organizationId: string,
  profileId: string,
) {
  return useQuery(profileDetailsQueryOptions(organizationId, profileId));
}
